import React, { useState, useEffect } from "react";

export default function Carousel(props) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Sample carousel data - you can pass this via props
  const slides = props.slides || [
    {
      id: 1,
      title: "AI-Powered Clinical Documentation",
      description: "Transform your clinical workflows with intelligent automation",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop",
      color: "from-blue-500 to-blue-700"
    },
    {
      id: 2,
      title: "Real-time Processing",
      description: "Instant document analysis and collaboration capabilities",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=400&fit=crop",
      color: "from-green-500 to-green-700"
    },
    {
      id: 3,
      title: "HIPAA-Compliant Security",
      description: "Enterprise-grade data protection for healthcare",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=400&fit=crop",
      color: "from-purple-500 to-purple-700"
    },
    {
      id: 4,
      title: "Advanced Analytics",
      description: "Gain insights into your clinical documentation patterns",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop",
      color: "from-indigo-500 to-indigo-700"
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {/* Carousel Container */}
      <div className="relative overflow-hidden rounded-xl shadow-2xl">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={slide.id} className="w-full flex-shrink-0">
              <div className={`relative h-80 bg-gradient-to-r ${slide.color} text-white`}>
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-20"
                  style={{ backgroundImage: `url(${slide.image})` }}
                />
                
                {/* Content */}
                <div className="relative z-10 flex items-center justify-center h-full px-8">
                  <div className="text-center">
                    <h3 className="text-3xl font-bold mb-4">{slide.title}</h3>
                    <p className="text-xl opacity-90 max-w-md">{slide.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition duration-200 backdrop-blur-sm"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition duration-200 backdrop-blur-sm"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition duration-200 ${
                index === currentSlide 
                  ? 'bg-white' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex justify-center space-x-4">
        <button
          onClick={toggleAutoPlay}
          className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
            isAutoPlaying
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          {isAutoPlaying ? '⏸️ Pause' : '▶️ Play'}
        </button>
        
        <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
          Slide {currentSlide + 1} of {slides.length}
        </div>
      </div>

      {/* Slide Info */}
      <div className="mt-4 text-center">
        <h4 className="text-lg font-semibold text-gray-900">
          {slides[currentSlide].title}
        </h4>
        <p className="text-gray-600 mt-1">
          {slides[currentSlide].description}
        </p>
      </div>
    </div>
  );
}
